import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

/**
 * Search input for filtering dashboard modules
 * Supports Ctrl+K keyboard shortcut to focus
 */
const DashboardSearch = forwardRef(({
  value,
  onChange,
  placeholder = 'Search modules...',
  resultCount = null,
  totalCount = null
}, ref) => {
  const inputRef = useRef(null);

  // Expose focus method via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  // Keyboard shortcut: Ctrl+K or Cmd+K to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      // Escape to clear search
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        onChange('');
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onChange]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextField
        inputRef={inputRef}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          width: { xs: '100%', sm: 300 },
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.paper',
            '&:hover fieldset': {
              borderColor: 'primary.main'
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main'
            }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    bgcolor: 'grey.100',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.7rem',
                    fontFamily: 'monospace'
                  }}
                >
                  Ctrl+K
                </Typography>
              )}
            </InputAdornment>
          )
        }}
      />

      {/* Result count indicator */}
      {value && resultCount !== null && totalCount !== null && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {resultCount} of {totalCount} modules
        </Typography>
      )}
    </Box>
  );
});

DashboardSearch.displayName = 'DashboardSearch';

export default DashboardSearch;
